import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { App } from './pages/App';
import { AuthCallback } from './pages/AuthCallback';
import { CssBaseline } from '@mui/material';
import { AppearanceProvider } from './theme/AppearanceProvider';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import { queryClient } from './lib/queryClientConfig';
import { NotificationProvider } from './components/NotificationProvider';
import './global.css';

const CHUNK_RELOAD_ATTEMPTED_KEY = 'chunk-reload-attempted';
const CHUNK_RELOAD_QUERY_PARAM = '__chunk_reload';

function isStaleChunkLoadError(value: unknown): boolean {
  const message: string =
    typeof value === 'string'
      ? value
      : value instanceof Error
        ? value.message
        : typeof (value as { message?: unknown } | null)?.message === 'string'
          ? (value as { message: string }).message
          : '';
  if (!message) {
    return false;
  }
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk')
  );
}

function attemptChunkRecoveryReload(): void {
  const hasReloaded: string | null = sessionStorage.getItem(CHUNK_RELOAD_ATTEMPTED_KEY);
  if (hasReloaded) {
    return;
  }
  sessionStorage.setItem(CHUNK_RELOAD_ATTEMPTED_KEY, 'true');
  const url: URL = new URL(window.location.href);
  url.searchParams.set(CHUNK_RELOAD_QUERY_PARAM, Date.now().toString());
  window.location.replace(url.toString());
}

// Handle stale chunk loading errors by auto-refreshing once.
// This occurs when a new deployment changes chunk hashes but the browser has old HTML/JS cached.
window.addEventListener('error', (event: ErrorEvent) => {
  if (!isStaleChunkLoadError(event.error ?? event.message)) {
    return;
  }
  attemptChunkRecoveryReload();
});

// Some browsers surface dynamic-import failures as promise rejections instead of window "error" events.
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  if (!isStaleChunkLoadError(event.reason)) {
    return;
  }
  attemptChunkRecoveryReload();
});

// Clear the reload flag and remove our cache-busting query param on successful page load.
window.addEventListener('load', () => {
  sessionStorage.removeItem(CHUNK_RELOAD_ATTEMPTED_KEY);
  const url: URL = new URL(window.location.href);
  if (!url.searchParams.has(CHUNK_RELOAD_QUERY_PARAM)) {
    return;
  }
  url.searchParams.delete(CHUNK_RELOAD_QUERY_PARAM);
  const cleanedUrl: string = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(null, '', cleanedUrl);
});


const container = document.getElementById('root')!;
const root = createRoot(container);

function RouterEntry() {
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  // Fallback: If Supabase redirects with URL hash on root path, handle here
  if (
    hash &&
    (hash.includes('access_token=') ||
      hash.includes('refresh_token=') ||
      hash.includes('code=') ||
      hash.includes('type='))
  ) {
    return (
      <QueryClientProvider client={queryClient}>
        <AppearanceProvider>
          <CssBaseline />
          <NotificationProvider>
            <AuthCallback />
          </NotificationProvider>
        </AppearanceProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <AppearanceProvider>
        <CssBaseline />
        <NotificationProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </NotificationProvider>
      </AppearanceProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

root.render(
  <>
    <RouterEntry />
    <Analytics />
    <SpeedInsights />
  </>,
);

// Cache bust 1761778221
